import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import { Alert } from '@twilio-paste/alert';
import { Text } from '@twilio-paste/text';
import { Box } from '@twilio-paste/box';
import { Button } from '@twilio-paste/button';
import { Anchor } from '@twilio-paste/anchor';
import { Card } from '@twilio-paste/card';
import { Heading } from '@twilio-paste/heading';
import { Paragraph } from '@twilio-paste/paragraph';
import { DoDont, Do, Dont } from '../../../components/DoDont';
import { Blockquote } from '../../../components/Blockquote';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { Codeblock } from '../../../components/codeblock';
import { SidebarCategoryRoutes } from '../../../constants';
import Changelog from '@twilio-paste/alert/CHANGELOG.md';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/alert"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/alert/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name={props.pageContext.frontmatter.title} categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/alert" storybookUrl="/?path=/story/components-alert" abstractUrl="https://share.goabstract.com/85c81258-90b9-49c8-b34b-9418da5f2f00" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <LivePreview scope={{
          Alert,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Alert variant="warning">
  <Text as="span">
    <strong>Chimamanda Ngozi Adichie:</strong> Racism should never have happened and so you don't get a cookie for reducing it.
  </Text>
</Alert>`}
        </LivePreview>
        <h2>{`Guidelines`}</h2>
        <h2>{`About Alerts`}</h2>
        <p>{`Use an alert to notify a user about high-priority or time-sensitive changes in the system. Alerts communicate system-level information that isn't triggered by a user action.`}</p>
        <p>{`Use an alert for one of these types of information:`}</p>
        <ul>
          <li parentName="ul"><strong parentName="li">{`Neutral or general information.`}</strong>{` Use this in most cases where you are notifying a user of a change.`}</li>
          <li parentName="ul"><strong parentName="li">{`Warning.`}</strong>{` Use this for critical information that will help a user avoid an issue.`}</li>
          <li parentName="ul"><strong parentName="li">{`Error.`}</strong>{` Use this for critical information that's preventing a user from continuing a flow and if there's action required on their end to resolve the issue.`}</li>
        </ul>
        <h3>{`Alerts vs. Feedback indicators and dialogs`}</h3>
        <p>{`To communicate feedback on a user action, use a toast or in-page validation (`}<em parentName="p">{`e.g.`}</em>{`, full-page confirmation, inline error) instead.`}</p>
        <p>{`To interrupt a user flow or provide the user with more than one action, use an alert dialog.`}</p>
        <h3>{`Accessibility`}</h3>
        <ul>
          <li parentName="ul">{`On all alerts, pass in `}<inlineCode parentName="li">{`role="alert"`}</inlineCode>{` or `}<inlineCode parentName="li">{`role="status"`}</inlineCode>{` through `}<a parentName="li" {...{
              "href": "#props"
            }}>{`props`}</a>{`.`}</li>
          <li parentName="ul">{`Screen readers will announce error and warning alert text immediately. Alerts that persist across multiple pages will announce themselves repeatedly which may become annoying. Consider making alerts in these cases dismissible.`}</li>
          <li parentName="ul">{`Render an alert only when it's needed. Assistive technologies will be able to read a visually hidden element. Don't visually hide an alert and then make it visible through client-side code.`}</li>
        </ul>
        <p>{`Our alerts have icons that both announce the type of alert it is to screen readers and visually indicate what type of alert it is. That way the background color isn't the only distinguishing feature between alert types.`}</p>
        <h2>{`Examples`}</h2>
        <p>{`Here are ways that you can compose neutral, warning, error, and dismissible alerts.`}</p>
        <Box marginBottom="space40" mdxType="Box">
  <Alert variant="neutral" mdxType="Alert">
    <Text as="span" mdxType="Text">
      <strong>Martin Luther King, Jr.:</strong> We may have all come on different ships, but we’re in the same boat now.
    </Text>
  </Alert>
        </Box>
        <Box marginBottom="space40" mdxType="Box">
  <Alert variant="warning" mdxType="Alert">
    <Text as="span" mdxType="Text">
      <strong>James Baldwin:</strong> Love takes off masks that we fear we cannot live without and know we cannot live
      within.
    </Text>
  </Alert>
        </Box>
        <Box marginBottom="space40" mdxType="Box">
  <Alert variant="error" mdxType="Alert">
    <Text as="span" mdxType="Text">
      <strong>Chimamanda Ngozi Adichie:</strong> Racism should never have happened and so you don't get a cookie for
      reducing it.
    </Text>
  </Alert>
        </Box>
        <Box marginBottom="space70" mdxType="Box">
  <Alert onDismiss={() => alert('dismissed')} variant="warning" mdxType="Alert">
    <Text as="span" mdxType="Text">
      <strong>Toni Morrison:</strong> You wanna fly, you got to give up the shit that weighs you down.
    </Text>
  </Alert>
        </Box>
        <h3>{`Neutral`}</h3>
        <p>{`Use a neutral alert to notify a user of a change that's relevant for them to achieve their current goal. Don't use an alert to communicate general promotional information.`}</p>
        <p>{`In most cases, a neutral alert should be dismissible (`}<em parentName="p">{`i.e.`}</em>{`, have a close button) and should be assigned `}<inlineCode parentName="p">{`role="status"`}</inlineCode></p>
        <Blockquote name="Nielsen Norman Group" source="Indicators, Validations, and Notifications" sourceUrl="https://www.nngroup.com/articles/indicators-validations-notifications/" mdxType="Blockquote">
  A notification sent irrespective of the current user goal would likely be ignored, and may even annoy users because it
  will disrupt their current task and be irrelevant to their current needs.
        </Blockquote>
        <LivePreview scope={{
          Alert,
          Text,
          Anchor
        }} language="jsx" mdxType="LivePreview">
  {`<Alert variant="neutral">
  <Text as="span">
    <strong>Set up your contact preferences.</strong> You can now set up your contact preferences on your dashboard. <Anchor href="#">Go to contact preferences</Anchor>
  </Text>
</Alert>`}
        </LivePreview>
        <h3>{`Warning`}</h3>
        <p>{`Use a warning alert to help a user avoid an issue. If the user can take an immediate action to avoid the issue, provide a way to do that.`}</p>
        <p>{`Be cautious about using a warning alert because they can be stressful for a user to see. In most cases, a warning alert should be assigned `}<inlineCode parentName="p">{`role="alert"`}</inlineCode>{`.`}</p>
        <LivePreview scope={{
          Alert,
          Text
        }} language="jsx" mdxType="LivePreview">
  {`<Alert variant="warning">
  <Text as="span">
    <strong>Some information on this page may be out-of-date.</strong> This page was last updated at 9:00 AM PST. We're currently looking into this issue.
  </Text>
</Alert>`}
        </LivePreview>
        <h3>{`Error`}</h3>
        <p>{`Use an error alert to help a user understand a critical issue at the system level. Provide a way to resolve the issue in the alert.`}</p>
        <p>{`Be cautious about using an error alert because they can be stressful for a user to see. In most cases, error alerts should be assigned `}<inlineCode parentName="p">{`role="error"`}</inlineCode>{`.`}</p>
        <p>{`There's a good chance you need to use an error alert if both these conditions are met:`}</p>
        <ul>
          <li parentName="ul">{`The system-level issue is preventing the user from continuing a flow.`}</li>
          <li parentName="ul">{`The user must take an action to resolve the issue.`}</li>
        </ul>
        <LivePreview scope={{
          Alert,
          Text,
          Anchor
        }} language="jsx" mdxType="LivePreview">
  {`<Alert variant="error">
  <Text as="span">
    <strong>Please update your billing information or we'll suspend your account on March 2.</strong> We couldn't renew your subscription with the information we currently have. <Anchor href="#">Update billing information</Anchor>
  </Text>
</Alert>`}
        </LivePreview>
        <h3>{`Dismissible`}</h3>
        <p>{`Add a close button to the alert if it doesn't communicate a persistent status of the system or the account. If you make an alert dismissible, provide another way for the user to retrieve the alert information.`}</p>
        <p>{`Don't add a close button to the alert if all these conditions are true:`}</p>
        <ul>
          <li parentName="ul">{`The alert communicates a persistent status of the system or the account.`}</li>
          <li parentName="ul">{`The information is relevant to the user's goals on the page(s) the alert is on.`}</li>
          <li parentName="ul">{`The user wouldn't be able to access the information in the alert if it were closed.`}</li>
        </ul>
        <p>{`Keep in mind that the longer an alert is present, the more it starts looking like part of the page and loses its interruptive intent. This might make users start to ignore important alert information.`}</p>
        <LivePreview scope={{
          Alert,
          Text,
          Anchor
        }} language="jsx" mdxType="LivePreview">
  {`<Alert onDismiss={() => alert('dismissed')} variant="neutral">
  <Text as="span">
    We've enabled new security options for your account. <Anchor href="#">Learn more</Anchor>
  </Text>
</Alert>`}
        </LivePreview>
        <h2>{`Composition Notes`}</h2>
        <p>{`Keep alert text brief by placing only the highest priority information in an alert. Too much text in an alert can overwhelm a user. A good rule of thumb is to keep alert text to a single line in a desktop-sized container, or limiting it to 90 characters.`}</p>
        <Blockquote name="Matthew Butterick" source="Butterick's Practical Typography" sourceUrl="https://practicaltypography.com/responsive-web-design.html" mdxType="Blockquote">
  Regardless of screen width, the optimal line length is still 45–90 characters. As you test your layout, make sure that
  text elements stay within this range.
        </Blockquote>
        <p>{`Compose an alert with:`}</p>
        <ul>
          <li parentName="ul"><strong parentName="li">{`A title (optional).`}</strong>{` Bold this text at the beginning of the alert. This should be the most scannable text in the alert.`}</li>
          <li parentName="ul"><strong parentName="li">{`Body text.`}</strong>{` Use the default text style. Provide specificity on the issue and a resolution if applicable via an `}<a parentName="li" {...{
              "href": "/components/anchor"
            }}>{`anchor`}</a>{`. Be cautious about using a button in an alert since it may compete with other buttons on the screen and should be used only to trigger an action. If you must use a button, use a `}<a parentName="li" {...{
              "href": "/components/button/#small-button"
            }}>{`small button`}</a>{`.`}</li>
        </ul>
        <h3>{`When to Use an Alert`}</h3>
        <p>{`Place an alert where it makes the most contextual sense. Don't cover other UI elements with an alert.`}</p>
        <p>{`In general, the persistence and behavior on scroll of an alert should match the element it's scoped to. For example, if an alert is scoped to a navigation bar that is sticky on scroll and persists across several pages, the alert should similarly be sticky and persist across several pages. However, don't persist the alert across too many pages in a user session since it may lose its interruptive intent.`}</p>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use only 1 alert per page. Use 2 alerts only if absolutely necessary and if the alerts are placed in different containers." mdxType="Do">
    <Alert variant="error" mdxType="Alert">
      <Text as="span" mdxType="Text">
        Your account balance is low and your credit card has expired. As a result we have suspended your account
      </Text>
    </Alert>
  </Do>
  <Dont title="Don't" body="Don't use more than 2 alerts per page." mdxType="Dont">
    <Alert variant="error" mdxType="Alert">
      <Text as="span" mdxType="Text">Your credit card has expired</Text>
    </Alert>
    <Alert variant="warning" mdxType="Alert">
      <Text as="span" mdxType="Text">Your account balance is low</Text>
    </Alert>
    <Alert variant="error" mdxType="Alert">
      <Text as="span" mdxType="Text">Your account is suspended</Text>
    </Alert>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Place global alerts at the top of the page. Place all other alerts at the top of the container it relates to beneath the heading." mdxType="Do">
    <Box padding="space40" mdxType="Box">
      <Card mdxType="Card">
        <Heading as="h2" variant="heading20" mdxType="Heading">
          Section Heading
        </Heading>
        <Box marginBottom="space70" mdxType="Box">
          <Alert variant="error" mdxType="Alert">There was a problem below</Alert>
        </Box>
        <Paragraph mdxType="Paragraph">Please fill in the details below...</Paragraph>
      </Card>
    </Box>
  </Do>
  <Dont title="Don't" body="Don't cover other UI elements with an alert." mdxType="Dont">
    <Box padding="space40" position="relative" mdxType="Box">
      <Heading as="h2" variant="heading10" mdxType="Heading">
        Billing information
      </Heading>
      <Box position="absolute" left="space90" top="0" mdxType="Box">
        <Alert variant="warning" mdxType="Alert">Your credit card is about to expire</Alert>
      </Box>
    </Box>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use anchors to link to more information that may be helpful for users to resolve or clarify an alert message." mdxType="Do">
    <Alert variant="neutral" mdxType="Alert">
      <Text as="span" mdxType="Text">
        A call to action <Anchor href="#" mdxType="Anchor">Learn more</Anchor>
      </Text>
    </Alert>
  </Do>
  <Dont title="Don't" body="Avoid using buttons in alerts unless it triggers an action." mdxType="Dont">
    <Alert variant="neutral" mdxType="Alert">
      <Text as="span" mdxType="Text">
        A call to action{' '}
        <Button size="small" variant="primary" mdxType="Button">
          Learn more
        </Button>
      </Text>
    </Alert>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Be cautious about using alerts since they can be stressful for users to see." center mdxType="Do" />
  <Dont title="Don't" body="Don't use alerts to communicate every change in a product (e.g., marketing messages, trial status) so that important messaging isn't diluted." center mdxType="Dont" />
        </DoDont>
        <h2>{`Anatomy`}</h2>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`background-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Error: color-background-error-lightest`}</li><li>{`Neutral: color-background-primary-lightest`}</li><li>{`Warning: color-background-warning-lightest`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Error: color-border-error-light`}</li><li>{`Neutral: color-border-neutral`}</li><li>{`Warning: color-border-warning-light`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-radius`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`border-radius-0`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`border-width`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Bottom border: border-width-20`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`icon color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Error: color-text-error`}</li><li>{`Neutral: color-text-neutral`}</li><li>{`Warning: color-text-warning`}</li><li>{`Close: color-text-icon`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`icon size`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Alert icon: size-icon-20`}</li><li>{`Close icon: size-icon-20`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`spacing`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><ul><li>{`Top padding: space-50`}</li><li>{`Bottom padding: space-50`}</li><li>{`left/right padding: space-60`}</li><li>{`Between icon and body: space-30`}</li><li>{`Between body and close button: space-60`}</li></ul></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/alert - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Alert} from '@twilio-paste/alert';

const Component = () => (
  <Alert variant="error">
    <Text>There is an error in the system.</Text>
  </Alert>
);
`}</code></pre>
        <h4>{`Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onDismiss?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`() => void`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Create dismissable alerts by providing an onDismiss callback`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`role?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Provide a specific ARIA role to the alert that overrides the calculated one set by the component`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`variant?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`error`}</inlineCode>{` / `}<inlineCode parentName="td">{`neutral`}</inlineCode>{` / `}<inlineCode parentName="td">{`warning`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      